import React, { useState } from "react";
import { PatientInfo as PatientInfoType } from "@/lib/dashboard/patientDashboardData";
import { Allergy } from "@/lib/dashboard/allergiesData";
import { Condition } from "@/lib/dashboard/conditionsData";
import { TabType, profileTabs } from "@/lib/dashboard/patientProfileConfig";
import { PatientInfo } from "./components/PatientInfo";
import { PatientAllergies } from "./components/PatientAllergies";
import { PatientConditions } from "./components/PatientConditions";
import { PatientNotes } from "../PatientNotes/PatientNotes";

interface PatientProfileProps {
  patientData: PatientInfoType;
  isDarkMode: boolean;
  onEditPatient?: () => void;
  onPrintSummary: () => void;
  onSaveAllergy: (allergyData: any) => void;
  onEditAllergy: (index: number, allergyData: any) => void;
  onDeleteAllergy: (index: number) => void;
  onSaveCondition: (conditionData: any) => void;
  onEditCondition: (index: number, conditionData: any) => void;
  onDeleteCondition: (index: number) => void;
  // determines whether user can modify allergies/conditions
  hasConsultationPermission?: boolean;
}

export function PatientProfile({
  patientData,
  isDarkMode,
  onEditPatient,
  onPrintSummary,
  onSaveAllergy,
  onEditAllergy,
  onDeleteAllergy,
  onSaveCondition,
  onEditCondition,
  onDeleteCondition,
  hasConsultationPermission = false,
}: PatientProfileProps) {
  const [activeTab, setActiveTab] = useState<TabType>("profile");
  return (
    <div
      className={`w-1/3 border-r overflow-hidden transition-colors duration-300 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      <div
        className={`border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <div className="flex">
          {profileTabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;

            return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? isDarkMode
                        ? "text-white border-b-2 border-black bg-gray-700"
                        : "text-gray-900 border-b-2 border-black bg-gray-50"
                      : isDarkMode
                      ? "text-gray-400 hover:text-gray-200 hover:bg-gray-750"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  {tab.label}
                </button>

            );
          })}
        </div>
      </div>
      <div className="overflow-y-auto h-full scrollbar-hover-thin">
        <div className="p-6">
          {activeTab === "profile" && (
            <>
              <PatientInfo
                patientData={patientData}
                isDarkMode={isDarkMode}
                onEditPatient={onEditPatient}
                onPrintSummary={onPrintSummary}
              />
              <PatientAllergies
                isDarkMode={isDarkMode}
                onSaveAllergy={onSaveAllergy}
                onEditAllergy={onEditAllergy}
                onDeleteAllergy={onDeleteAllergy}
                hasConsultationPermission={hasConsultationPermission}
              />
              <PatientConditions
                isDarkMode={isDarkMode}
                onSaveCondition={onSaveCondition}
                onEditCondition={onEditCondition}
                onDeleteCondition={onDeleteCondition}
                hasConsultationPermission={hasConsultationPermission}
              />
            </>
          )}
          {activeTab === "notes" && (
            <PatientNotes
              patientId={patientData.nhsNumber}
              patientEmail={patientData.email}
              patientPhone={patientData?.contacts?.find(c => c?.value)?.value || ""}
              isDarkMode={isDarkMode}
            />
          )}
        </div>
      </div>
    </div>
  );
}
